/**
 * `<Button />` — Story 5.4 (UX §4.1 line 879, AC line 1006).
 *
 * Four-variant button primitive consumed by every Epic 6+ surface
 * (admin shell save/cancel CTAs, submitter wizard "next/upload",
 * voter "submit" affordances, reveal control "begin/next/previous"
 * keys).
 *
 * Variants:
 *   - `primary`   — `--brand-primary` background; "save / submit / continue".
 *   - `secondary` — `--bg-surface-raised` + `--border-strong`; "cancel / back".
 *   - `ghost`     — transparent; "tertiary inline action".
 *   - `danger`    — `--danger` background; UX §6.6 confirmation pattern.
 *
 * Contract:
 *   - `type` defaults to `"button"` (NOT `"submit"`) so dropping a Button
 *     inside a `<form>` does not accidentally submit. Story 6.5 wizard
 *     consumers opt in via `type="submit"` explicitly.
 *   - `loading={true}` disables the button, swaps the visible label for
 *     a spinning SVG, and preserves the original label on `aria-label`
 *     so screen readers still announce the action.
 *   - Focus ring contract (Critical Scope Decision #6): full
 *     `outlineWidth: 2 / outlineStyle: 'solid' / outlineColor:
 *     var(--brand-primary) / outlineOffset: 2`.
 *   - 44px min-height (UX §5.1 line 992).
 *   - `prefers-reduced-motion` reduces the spinner rotation to a static
 *     glyph (CSS `@media` rule below; identical pattern to HeartButton).
 *
 * NOT supported in this story: polymorphic `as` prop. Consumers needing
 * link-styled-as-button compose `<a className="...">` with their own
 * styling. Future story may add an `as` prop if demand materializes.
 */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

/**
 * Story 25.2 — `loadingVariant` controls how the loading state is
 * visually presented:
 *   - `'replace'` (default, legacy behavior): swap the label for the
 *     spinner. Compact; preserves label on aria-label for SR users.
 *   - `'inline'`: render the spinner BEFORE the label. Keeps the
 *     contextual label visible so the user reads what they triggered
 *     while the spinner animates. Used by the EventDashboardPage
 *     lifecycle buttons where label-context is load-bearing.
 *
 * Default stays `'replace'` so all existing consumers are unchanged.
 */
export type ButtonLoadingVariant = 'replace' | 'inline';

export interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  /** One of the four UX §4.1 variants. */
  variant: ButtonVariant;
  /** When true, button disables and shows a spinner; original label preserved on aria-label. */
  loading?: boolean;
  /**
   * Story 25.2 — controls spinner-vs-label layout while loading.
   * Defaults to `'replace'` (legacy behavior).
   */
  loadingVariant?: ButtonLoadingVariant;
  /** Defaults to `'button'` to prevent accidental form submissions. */
  type?: 'button' | 'submit' | 'reset';
  children?: ReactNode;
}

const TAP_TARGET_MIN_PX = 44;
const SPINNER_ANIMATION_NAME = 'pvt-button-spinner';

// CSS @keyframes for the loading-state spinner. Emitted once per page
// via a top-of-module <style> block; idempotent (same name + same rule
// across multiple Button mounts is a no-op for the browser). Reduced-
// motion override caps the rotation to a static glyph.
const SPINNER_STYLE = `
@keyframes ${SPINNER_ANIMATION_NAME} {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
@media (prefers-reduced-motion: reduce) {
  .${SPINNER_ANIMATION_NAME} {
    animation: none !important;
  }
}
`;

function variantStyle(variant: ButtonVariant): {
  background: string;
  color: string;
  border: string;
} {
  switch (variant) {
    case 'primary':
      return {
        background: 'var(--brand-primary)',
        color: 'var(--text-on-brand)',
        border: '1px solid transparent',
      };
    case 'secondary':
      return {
        background: 'var(--bg-surface-raised)',
        color: 'var(--text-primary)',
        border: '1px solid var(--border-strong)',
      };
    case 'ghost':
      return {
        background: 'transparent',
        color: 'var(--text-primary)',
        border: '1px solid transparent',
      };
    case 'danger':
      return {
        background: 'var(--danger)',
        color: 'var(--text-on-brand)',
        border: '1px solid transparent',
      };
  }
}

function Spinner() {
  return (
    <svg
      className={SPINNER_ANIMATION_NAME}
      width={16}
      height={16}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
      data-testid="button-spinner"
      style={{
        animation: `${SPINNER_ANIMATION_NAME} 800ms linear infinite`,
      }}
    >
      <circle cx="8" cy="8" r="6" opacity="0.25" />
      <path d="M14 8a6 6 0 0 0-6-6" />
    </svg>
  );
}

/**
 * `Button` — typed wrapper around `<button>` enforcing the UX §4.1
 * variant contract.
 *
 * @param variant   Required visual variant.
 * @param loading   When true, shows spinner + disables interaction.
 * @param disabled  When true, visually muted + cursor: not-allowed.
 * @param type      `'button' | 'submit' | 'reset'`; defaults to `'button'`.
 * @param children  Label text or inline node.
 */
export function Button({
  variant,
  loading = false,
  loadingVariant = 'replace',
  disabled = false,
  type = 'button',
  children,
  style,
  'aria-label': ariaLabel,
  ...rest
}: ButtonProps) {
  const v = variantStyle(variant);
  const effectiveDisabled = disabled || loading;

  // When loading, preserve the original label on aria-label so screen
  // readers still announce the action even though the visible content
  // is the spinner SVG. When children isn't a string (e.g., contains
  // icons), fall back to a generic "Loading…" announcement so the
  // button is never accessible-name-less in the loading state.
  // Story 25.2: in 'inline' loadingVariant the visible label is still
  // rendered, so an explicit aria-label override is unnecessary; only
  // override for the legacy 'replace' variant where the label vanishes.
  let computedAriaLabel = ariaLabel;
  if (loading && loadingVariant === 'replace' && computedAriaLabel === undefined) {
    computedAriaLabel = typeof children === 'string' ? children : 'Loading…';
  }

  return (
    <>
      <style data-testid="button-keyframes">{SPINNER_STYLE}</style>
      <button
        {...rest}
        type={type}
        disabled={effectiveDisabled}
        aria-disabled={effectiveDisabled || undefined}
        aria-busy={loading || undefined}
        aria-label={computedAriaLabel}
        style={{
          minHeight: TAP_TARGET_MIN_PX,
          padding: '0 var(--space-4)',
          background: v.background,
          color: v.color,
          border: v.border,
          borderRadius: 'var(--radius-md)',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-base-size)',
          fontWeight: 500,
          cursor: effectiveDisabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.4 : 1,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-2)',
          // Focus-ring contract per Critical Scope Decision #6.
          outlineWidth: 2,
          outlineStyle: 'solid',
          outlineColor: 'var(--brand-primary)',
          outlineOffset: 2,
          ...style,
        }}
      >
        {loading
          ? loadingVariant === 'inline'
            ? (
              <>
                <Spinner />
                {children}
              </>
            )
            : <Spinner />
          : children}
      </button>
    </>
  );
}
